import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { SeatingApi } from '../services/SeatingApiClient';
import { errorMessageOf, isApiError } from '../utils/apiErrors';
import { statusOfError, withGuest, type DoorStatus } from './useDoor';
import { seatedCounts, usableTables } from '../utils/seating';
import type { DoorGuest } from '../types/Door';
import type { SeatingTable, SeatingWaiter } from '../types/Seating';

export const SEATING_POLL_MS = 30_000;

/** A seat the table refused because it is full: kept so a planner can confirm seating the guest anyway. */
export interface FullRefusal {
  guestId: string;
  table: SeatingTable;
  seated: number;
  /** The planner already chose to seat this declined guest, so the retry keeps that choice. */
  allowDeclined: boolean;
}

/** What a planner has chosen to override for one seat. */
export interface SeatOverride { allowOverCapacity?: boolean; allowDeclined?: boolean }

export interface UseSeating {
  status: DoorStatus;
  error: unknown;
  /** True when a refresh failed but the last list is still shown. */
  stale: boolean;
  guests: DoorGuest[];
  /** Tables usable at this event (project-wide ones and this event's own), by number. */
  tables: SeatingTable[];
  waiters: SeatingWaiter[];
  seated: Map<string, number>;
  busyId: string | null;
  actionError: string | null;
  fullRefusal: FullRefusal | null;
  refresh: () => Promise<void>;
  /** Seats the guest at the table, or clears their table when tableId is null. allowDeclined seats a guest who declined. */
  assign: (guestId: string, tableId: string | null, override?: { allowDeclined?: boolean }) => Promise<void>;
  seatAnyway: () => Promise<void>;
  dismissFull: () => void;
  clearActionError: () => void;
}

/**
 * Seating for one event: the guest list with tables, the tables usable at the event, and the waiters. A planner changes a seat
 * with assign(); a full table is reported as fullRefusal instead of being forced. Refreshes slowly while the tab is visible.
 */
export function useSeating(api: SeatingApi, eventId: string | null, pollMs: number = SEATING_POLL_MS): UseSeating {
  const [status, setStatus] = useState<DoorStatus>('loading');
  const [error, setError] = useState<unknown>(null);
  const [stale, setStale] = useState(false);
  const [guests, setGuests] = useState<DoorGuest[]>([]);
  const [allTables, setAllTables] = useState<SeatingTable[]>([]);
  const [waiters, setWaiters] = useState<SeatingWaiter[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [fullRefusal, setFullRefusal] = useState<FullRefusal | null>(null);
  const loaded = useRef(false);
  const inFlight = useRef(false);
  const busy = useRef(false);
  const alive = useRef(true);
  const generation = useRef(0);

  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

  const refresh = useCallback(async () => {
    if (!eventId || inFlight.current) return;
    inFlight.current = true;
    const mine = generation.current;
    try {
      // The waiters only name who looks after a table: without them the screen still works.
      const [list, tableRows, waiterRows] = await Promise.all([api.listGuests(eventId), api.listTables(), api.listWaiters().catch(() => null)]);
      if (!alive.current || mine !== generation.current) return;
      // A seat change is in flight: its answer is newer than this list, so this one is dropped and the next poll catches up.
      if (!busy.current) setGuests(list);
      setAllTables(tableRows);
      if (waiterRows) setWaiters(waiterRows);
      loaded.current = true;
      setStatus('ready'); setError(null); setStale(false);
    } catch (err) {
      if (!alive.current || mine !== generation.current) return;
      const next = statusOfError(err);
      const offline = isApiError(err) && err.offline;
      if (loaded.current && (offline || next === 'unavailable')) { setStale(true); return; }
      setStatus(next); setError(err);
    } finally {
      inFlight.current = false;
    }
  }, [api, eventId]);

  useEffect(() => {
    generation.current += 1;
    inFlight.current = false;
    loaded.current = false;
    setStatus('loading'); setGuests([]); setStale(false); setError(null); setActionError(null); setFullRefusal(null); setBusyId(null);
    if (eventId) void refresh();
  }, [eventId, refresh]);

  useEffect(() => {
    if (!eventId || pollMs <= 0) return undefined;
    const tick = () => { if (typeof document === 'undefined' || !document.hidden) void refresh(); };
    const timer = setInterval(tick, pollMs);
    window.addEventListener('online', tick);
    document.addEventListener('visibilitychange', tick);
    return () => {
      clearInterval(timer);
      window.removeEventListener('online', tick);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [eventId, pollMs, refresh]);

  const tables = useMemo(() => (eventId ? usableTables(allTables, eventId) : []), [allTables, eventId]);
  const seated = useMemo(() => seatedCounts(guests), [guests]);

  const run = useCallback(async (guestId: string, tableId: string | null, override: SeatOverride) => {
    if (!eventId) return;
    setBusyId(guestId); busy.current = true; setActionError(null); setFullRefusal(null);
    try {
      const chosen: SeatOverride = {
        ...(override.allowOverCapacity ? { allowOverCapacity: true } : {}),
        ...(override.allowDeclined ? { allowDeclined: true } : {}),
      };
      const updated = tableId === null
        ? await api.unseat(eventId, guestId)
        : await api.seat(eventId, guestId, tableId, Object.keys(chosen).length > 0 ? chosen : undefined);
      if (alive.current) setGuests((cur) => withGuest(cur, updated));
    } catch (err) {
      if (!alive.current) return;
      const table = tableId ? tables.find((t) => t.id === tableId) : undefined;
      if (isApiError(err) && err.status === 409 && err.code === 'table_full' && table) {
        setFullRefusal({ guestId, table, seated: seated.get(table.id) ?? 0, allowDeclined: override.allowDeclined === true });
      } else {
        setActionError(errorMessageOf(err, 'Could not change the seating. Please try again.'));
      }
    } finally {
      busy.current = false;
      if (alive.current) setBusyId(null);
    }
  }, [api, eventId, tables, seated]);

  const assign = useCallback((guestId: string, tableId: string | null, override: { allowDeclined?: boolean } = {}) => run(guestId, tableId, override), [run]);
  const seatAnyway = useCallback(async () => {
    if (fullRefusal) await run(fullRefusal.guestId, fullRefusal.table.id, { allowOverCapacity: true, allowDeclined: fullRefusal.allowDeclined });
  }, [fullRefusal, run]);

  return {
    status, error, stale, guests, tables, waiters, seated, busyId, actionError, fullRefusal,
    refresh, assign, seatAnyway,
    dismissFull: useCallback(() => setFullRefusal(null), []),
    clearActionError: useCallback(() => setActionError(null), []),
  };
}
